import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchQuickMemos, createQuickMemo, updateQuickMemo, deleteQuickMemo } from '../api';

const DRAFT_STORAGE_KEY = 'quickMemo.draft';
const BACKEND_DEBOUNCE_MS = 800;
const LOCAL_DEBOUNCE_MS = 400;
const ERROR_TOAST_THROTTLE_MS = 4000;

const COLORS = [
  { key: 'yellow', swatch: 'bg-yellow-300', bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-900' },
  { key: 'pink', swatch: 'bg-pink-300', bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-900' },
  { key: 'blue', swatch: 'bg-blue-300', bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-900' },
  { key: 'green', swatch: 'bg-green-300', bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900' },
];
const COLOR_BY_KEY = COLORS.reduce((acc, c) => ({ ...acc, [c.key]: c }), {});

function readLocalDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeLocalDraft(content, color) {
  try {
    if (!content || !content.trim()) {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } else {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ content, color, savedAt: Date.now() }));
    }
  } catch (_) {
    // private mode / quota — the backend autosave still covers persistence
  }
}

function clearLocalDraft() {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch (_) {
    // noop
  }
}

/**
 * Global personal scratchpad — the digital equivalent of a sticky note.
 * Mounted once in App.js, inside the internal staff app shell only (not the
 * Client Portal or Auditor Portal, per explicit scope decision). Opened from
 * anywhere in that shell via Ctrl+Alt+M; portaled to document.body with a
 * high z-index so it renders above any modal that's already open.
 *
 * No Save button: content autosaves straight to the backend on a short
 * debounce, with a separate, independent localStorage mirror as a safety net
 * for the specific failure mode the backend autosave itself can't cover (a
 * dropped connection, or closing before the debounce fires).
 */
function QuickMemoWidget({ authAxios, showFormMessage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [memos, setMemos] = useState([]);
  const [draftContent, setDraftContentState] = useState('');
  const [draftColor, setDraftColorState] = useState('yellow');
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContentState] = useState('');
  const [editingColor, setEditingColorState] = useState('yellow');

  const panelRef = useRef(null);
  const textareaRef = useRef(null);
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  const draftIdRef = useRef(null);
  const draftContentRef = useRef('');
  const draftColorRef = useRef('yellow');
  const editingIdRef = useRef(null);
  const editingContentRef = useRef('');
  const editingColorRef = useRef('yellow');
  const backendDebounceRef = useRef(null);
  const localDebounceRef = useRef(null);
  const lastErrorToastAtRef = useRef(0);

  const setDraftContent = (v) => { draftContentRef.current = v; setDraftContentState(v); };
  const setDraftColor = (v) => { draftColorRef.current = v; setDraftColorState(v); };
  const setEditingContent = (v) => { editingContentRef.current = v; setEditingContentState(v); };
  const setEditingColor = (v) => { editingColorRef.current = v; setEditingColorState(v); };

  const notifyError = useCallback((message) => {
    const now = Date.now();
    if (now - lastErrorToastAtRef.current < ERROR_TOAST_THROTTLE_MS) return;
    lastErrorToastAtRef.current = now;
    if (showFormMessage) showFormMessage('danger', message);
  }, [showFormMessage]);

  const refreshList = useCallback(async () => {
    try {
      const data = await fetchQuickMemos(authAxios);
      setMemos(Array.isArray(data) ? data : []);
    } catch (_) {
      // Non-fatal — the compose box and any in-progress edit stay usable
      // even if the list itself fails to refresh.
    }
  }, [authAxios]);

  // Continuous autosave — no Save button. Always reads from refs (not a
  // closed-over state param) so it saves the truly-latest content whether
  // it's invoked by the debounce timer or an immediate flush from a
  // once-registered listener (global keydown / outside-click).
  const saveDraftNow = useCallback(async () => {
    const content = draftContentRef.current;
    const color = draftColorRef.current;
    if (!content || !content.trim()) {
      if (draftIdRef.current) {
        const id = draftIdRef.current;
        draftIdRef.current = null;
        try {
          await deleteQuickMemo(authAxios, id);
          await refreshList();
        } catch (_) {
          notifyError('Could not save memo.');
        }
      }
      return;
    }
    try {
      if (draftIdRef.current) {
        await updateQuickMemo(authAxios, draftIdRef.current, { content, color });
      } else {
        const created = await createQuickMemo(authAxios, { content, color });
        draftIdRef.current = created.id;
      }
      clearLocalDraft();
      await refreshList();
    } catch (_) {
      notifyError('Could not save memo.');
    }
  }, [authAxios, notifyError, refreshList]);

  const flushDraft = useCallback(() => {
    clearTimeout(backendDebounceRef.current);
    clearTimeout(localDebounceRef.current);
    return saveDraftNow();
  }, [saveDraftNow]);

  // Inline edit on an existing memo saves on blur — but a mousedown outside
  // the whole panel (closePopup) can start tearing the panel down before
  // that blur naturally fires, so closePopup() flushes this directly too.
  const flushInlineEdit = useCallback(async () => {
    const id = editingIdRef.current;
    if (!id) return;
    const content = editingContentRef.current;
    const color = editingColorRef.current;
    editingIdRef.current = null;
    if (!content || !content.trim()) return; // don't blank an existing note via inline edit
    try {
      await updateQuickMemo(authAxios, id, { content, color });
      await refreshList();
    } catch (_) {
      notifyError('Could not save memo.');
    }
  }, [authAxios, notifyError, refreshList]);

  const closePopup = useCallback(async () => {
    await flushInlineEdit();
    setEditingId(null);
    await flushDraft();
    setIsOpen(false);
  }, [flushInlineEdit, flushDraft]);

  // Global shortcut (Ctrl+Alt+M) + Escape-while-open, both in the capture
  // phase. Escape calls stopPropagation() so this codebase's other
  // bubble-phase document/window Escape listeners (ConfirmProvider, Drawer,
  // NotificationList) never see the keypress while this popup is topmost —
  // capture-phase listeners always run before the event ever reaches the
  // bubble phase, so stopping it here is what keeps Esc from also closing
  // whatever's behind this popup.
  useEffect(() => {
    const handleKeyDown = (e) => {
      // AltGr (common on European keyboard layouts) is reported by the
      // browser as ctrlKey+altKey — indistinguishable from a real chord
      // without this check. Not fully solvable everywhere getModifierState
      // isn't supported; documented, not silently patched over.
      if (e.getModifierState && e.getModifierState('AltGraph')) return;
      if (!isOpenRef.current) {
        if (e.ctrlKey && e.altKey && !e.shiftKey && !e.metaKey && e.code === 'KeyM') {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        closePopup();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [closePopup]);

  // Outside click while open — there's no dimmed backdrop to catch this via
  // onClick, so it's a document-level mousedown check instead.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        closePopup();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, closePopup]);

  // On open: a fresh compose session (recovering a dropped localStorage
  // draft if one exists), fetch the list, focus the textarea.
  useEffect(() => {
    if (!isOpen) return;
    draftIdRef.current = null;
    const recovered = readLocalDraft();
    if (recovered && recovered.content && recovered.content.trim()) {
      setDraftContent(recovered.content);
      setDraftColor(recovered.color || 'yellow');
    } else {
      setDraftContent('');
      setDraftColor('yellow');
    }
    refreshList();
    if (textareaRef.current) textareaRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Debounced backend autosave.
  useEffect(() => {
    if (!isOpen) return undefined;
    backendDebounceRef.current = setTimeout(saveDraftNow, BACKEND_DEBOUNCE_MS);
    return () => clearTimeout(backendDebounceRef.current);
  }, [draftContent, draftColor, isOpen, saveDraftNow]);

  // Debounced localStorage safety net — independent of the backend autosave,
  // specifically for the case where that autosave itself can't reach the
  // server (offline, dropped connection, a crash before it fires).
  useEffect(() => {
    if (!isOpen) return undefined;
    localDebounceRef.current = setTimeout(() => writeLocalDraft(draftContent, draftColor), LOCAL_DEBOUNCE_MS);
    return () => clearTimeout(localDebounceRef.current);
  }, [draftContent, draftColor, isOpen]);

  // Flush on unmount too, in case the whole widget goes away (e.g. logout)
  // while the popup is open with unsaved content.
  useEffect(() => () => {
    if (isOpenRef.current) {
      flushInlineEdit();
      flushDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComposeKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    if (!draftContentRef.current || !draftContentRef.current.trim()) return; // no-op on an empty textarea
    clearTimeout(backendDebounceRef.current);
    clearTimeout(localDebounceRef.current);
    saveDraftNow().then(() => {
      draftIdRef.current = null;
      clearLocalDraft();
      setDraftContent('');
      setDraftColor('yellow');
      if (textareaRef.current) textareaRef.current.focus();
    });
  };

  const startInlineEdit = (memo) => {
    editingIdRef.current = memo.id;
    setEditingId(memo.id);
    setEditingContent(memo.content);
    setEditingColor(memo.color);
  };

  const handleInlineEditBlur = () => {
    flushInlineEdit();
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    setMemos((prev) => prev.filter((m) => m.id !== id));
    try {
      await deleteQuickMemo(authAxios, id);
    } catch (_) {
      notifyError('Could not delete memo.');
      refreshList();
    }
  };

  if (!isOpen) return null;

  const visibleMemos = memos.filter((m) => m.id !== draftIdRef.current);

  return createPortal(
    <div
      ref={panelRef}
      className="fixed bottom-6 right-6 z-[9999] w-[380px] max-h-[80vh] flex flex-col rounded-lg shadow-xl border border-yellow-300 bg-yellow-50 overflow-hidden"
      role="dialog"
      aria-label="Quick Memo"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-yellow-200 bg-yellow-100">
        <span className="text-sm font-semibold text-yellow-900">Quick Memo</span>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {COLORS.map((c) => (
              <button
                key={c.key}
                type="button"
                title={c.key}
                aria-label={`Set note color to ${c.key}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setDraftColor(c.key)}
                className={`w-4 h-4 rounded-full ${c.swatch} ${draftColor === c.key ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={closePopup}
            aria-label="Close Quick Memo"
            className="text-yellow-800 hover:text-yellow-950 text-sm leading-none"
          >
            &#10005;
          </button>
        </div>
      </div>

      <div className="p-3 border-b border-yellow-200">
        <textarea
          ref={textareaRef}
          value={draftContent}
          onChange={(e) => setDraftContent(e.target.value)}
          onKeyDown={handleComposeKeyDown}
          placeholder="Type a note... (Enter to save and start a new one)"
          rows={4}
          className="w-full resize-none rounded-md border border-yellow-300 bg-white px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {visibleMemos.length === 0 && (
          <p className="text-xs text-gray-500 text-center py-4">No other notes yet.</p>
        )}
        {visibleMemos.map((memo) => {
          const palette = COLOR_BY_KEY[memo.color] || COLOR_BY_KEY.yellow;
          const isEditing = editingId === memo.id;
          return (
            <div key={memo.id} className={`rounded-md border ${palette.border} ${palette.bg} p-2`}>
              {isEditing ? (
                <>
                  <textarea
                    autoFocus
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    onBlur={handleInlineEditBlur}
                    rows={3}
                    className="w-full resize-none rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                  <div className="flex items-center gap-1 mt-1">
                    {COLORS.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        title={c.key}
                        aria-label={`Set note color to ${c.key}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setEditingColor(c.key)}
                        className={`w-4 h-4 rounded-full ${c.swatch} ${editingColor === c.key ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-start justify-between gap-2">
                  <p
                    className={`text-sm whitespace-pre-wrap break-words ${palette.text} cursor-text`}
                    onClick={() => startInlineEdit(memo)}
                  >
                    {memo.content}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleDelete(memo.id)}
                    aria-label="Delete memo"
                    className={`shrink-0 ${palette.text} opacity-60 hover:opacity-100 text-xs leading-none`}
                  >
                    &#10005;
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

export default QuickMemoWidget;
